import React from 'react';
import {
  FileText,
  CreditCard,
  RotateCcw,
  AlertCircle,
  Calendar,
  DollarSign,
  Shield,
  Gavel,
  RefreshCw,
  XCircle,
  Mail,
  Clock,
} from 'lucide-react';

const TermsOfService = () => {
  const lastUpdated = 'Janeiro de 2025';

  return (
    <section id='termos-uso' className='py-40 bg-gray-50'>
      <div className='container mx-auto px-4'>
        {/* Header */}
        <div className='text-center mb-16'>
          <div className='inline-flex items-center bg-purple-100 text-purple-600 px-6 py-3 rounded-full font-semibold mb-6'>
            <FileText className='w-5 h-5 mr-2' />
            Termos de Uso
          </div>

          <h2 className='text-4xl lg:text-5xl font-bold mb-6'>
            Termos de Uso e
            <span className='gradient-text block'>Política de Assinatura</span>
          </h2>

          <p className='text-xl text-gray-600 max-w-3xl mx-auto'>
            Estes termos regulam o uso do FinZiin e nossas assinaturas Pro. Leia
            atentamente antes de usar o aplicativo.
          </p>
        </div>

        {/* Content */}
        <div className='max-w-4xl mx-auto'>
          <div className='bg-white rounded-2xl shadow-xl overflow-hidden'>
            {/* Quick Info - Subscription */}
            <div className='bg-gradient-to-r from-purple-500 to-blue-500 text-white p-8'>
              <div className='grid md:grid-cols-3 gap-6'>
                <div className='text-center'>
                  <CreditCard className='w-8 h-8 mx-auto mb-3' />
                  <h3 className='font-bold mb-2'>Assinatura Pro</h3>
                  <p className='text-purple-100 text-sm'>
                    Experiência sem anúncios
                  </p>
                </div>
                <div className='text-center'>
                  <RefreshCw className='w-8 h-8 mx-auto mb-3' />
                  <h3 className='font-bold mb-2'>Renovação Automática</h3>
                  <p className='text-purple-100 text-sm'>
                    Planos mensais ou anuais renovados automaticamente via
                    Google Play
                  </p>
                </div>
                <div className='text-center'>
                  <XCircle className='w-8 h-8 mx-auto mb-3' />
                  <h3 className='font-bold mb-2'>Cancelamento Simples</h3>
                  <p className='text-purple-100 text-sm'>
                    Cancelamento direto pela Google Play a qualquer momento
                  </p>
                </div>
              </div>
            </div>

            <div className='p-8 space-y-8'>
              {/* 1. Aceitação dos Termos */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <FileText className='w-6 h-6 mr-3 text-purple-500' />
                  1. Aceitação dos Termos
                </h3>

                <div className='space-y-4 text-gray-600'>
                  <p>
                    Ao usar o FinZiin, você concorda integralmente com estes
                    Termos de Uso. Caso não concorde, não utilize o aplicativo.
                  </p>

                  <div className='bg-purple-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-purple-800 mb-2'>
                      Importante:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm text-purple-700'>
                      <li>
                        Ter pelo menos 18 anos ou permissão dos responsáveis
                      </li>
                      <li>Manter credenciais seguras e privadas</li>
                      <li>Usar o app de forma legal e responsável</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 2. Descrição do Serviço */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Shield className='w-6 h-6 mr-3 text-blue-500' />
                  2. Descrição do Serviço
                </h3>

                <div className='bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg mb-6'>
                  <div className='flex items-start space-x-3'>
                    <Shield className='w-5 h-5 text-blue-600 mt-0.5' />
                    <div>
                      <h4 className='font-bold text-blue-800 mb-2'>
                        🎯 Modelo Transparente
                      </h4>
                      <p className='text-blue-700 text-sm'>
                        Todas as funcionalidades essenciais do FinZiin são
                        gratuitas. Com a assinatura Pro, você remove anúncios e
                        apoia o desenvolvimento do aplicativo.
                      </p>
                    </div>
                  </div>
                </div>

                <div className='grid md:grid-cols-2 gap-6'>
                  {/* FinZiin Gratuito */}
                  <div className='bg-green-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-green-800 mb-3'>
                      ✅ FinZiin - Gratuito
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm text-green-700'>
                      <li>Todas as funcionalidades essenciais do app</li>
                      <li>Contas, cartões e relatórios ilimitados</li>
                      <li>Metas e previsões financeiras personalizadas</li>
                      <li>Backup completo na nuvem</li>
                      <li>Anúncios discretos (1 a cada 30 minutos)</li>
                      <li>Suporte via email</li>
                    </ul>
                  </div>

                  {/* FinZiin Pro */}
                  <div className='bg-purple-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-purple-800 mb-3'>
                      ⭐ FinZiin - Pro
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm text-purple-700'>
                      <li>Mesmas funcionalidades do FinZiin - Gratuito</li>
                      <li>Experiência sem anúncios</li>
                      <li>Renovação automática via Google Play</li>
                      <li>Apoie o desenvolvimento contínuo do FinZiin</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 3. Assinaturas e Cobrança */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <CreditCard className='w-6 h-6 mr-3 text-green-500' />
                  3. Assinaturas e Cobrança
                </h3>

                <div className='space-y-6'>
                  {/* Planos */}
                  <div>
                    <h4 className='text-xl font-semibold mb-4 text-gray-800'>
                      Planos Disponíveis:
                    </h4>
                    <div className='grid md:grid-cols-2 gap-4'>
                      <div className='border border-blue-200 rounded-lg p-4'>
                        <div className='flex items-center mb-3'>
                          <Calendar className='w-5 h-5 text-blue-500 mr-2' />
                          <h5 className='font-bold text-blue-800'>Mensal</h5>
                        </div>
                        <ul className='text-sm text-gray-600 space-y-1'>
                          <li>Renovação automática a cada 30 dias</li>
                          <li>Cobrança mensal via Google Play</li>
                          <li>Cancelamento a qualquer momento</li>
                        </ul>
                      </div>

                      <div className='border border-green-200 rounded-lg p-4'>
                        <div className='flex items-center mb-3'>
                          <Calendar className='w-5 h-5 text-green-500 mr-2' />
                          <h5 className='font-bold text-green-800'>Anual</h5>
                        </div>
                        <ul className='text-sm text-gray-600 space-y-1'>
                          <li>Renovação automática a cada 12 meses</li>
                          <li>Desconto de até 25%</li>
                          <li>Cobrança anual única via Google Play</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Renovação Automática */}
                  <div className='bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-lg'>
                    <div className='flex items-start space-x-3'>
                      <RefreshCw className='w-5 h-5 text-yellow-600 mt-0.5' />
                      <div>
                        <h4 className='font-bold text-yellow-800 mb-2'>
                          ⚠️ Renovação Automática
                        </h4>
                        <ul className='text-sm text-yellow-700 space-y-1'>
                          <li>
                            As assinaturas são renovadas automaticamente via
                            Google Play
                          </li>
                          <li>Cobrança realizada 24h antes do vencimento</li>
                          <li>Você receberá notificação antes da renovação</li>
                          <li>
                            Cancelamento é feito diretamente pela Google Play
                            Store
                          </li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Preços */}
                  <div className='bg-blue-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-blue-800 mb-2'>
                      💰 Informações de Preço:
                    </h4>
                    <ul className='text-sm text-blue-700 space-y-1'>
                      <li>Preços em Reais (BRL) na Google Play Store</li>
                      <li>
                        Podem variar por região e incluir impostos aplicáveis
                      </li>
                      <li>
                        Ofertas promocionais podem ocorrer por tempo limitado
                      </li>
                      <li>
                        Confira a Google Play Store para preços atualizados
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 4. Cancelamento e Reembolso */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <XCircle className='w-6 h-6 mr-3 text-red-500' />
                  4. Cancelamento e Reembolso
                </h3>

                <div className='space-y-4'>
                  <div>
                    <h4 className='font-semibold text-gray-800 mb-3'>
                      Como cancelar:
                    </h4>
                    <div className='bg-gray-50 p-4 rounded-lg'>
                      <ol className='list-decimal list-inside space-y-2 text-sm text-gray-600'>
                        <li>Abra a Google Play Store</li>
                        <li>Acesse Menu → Assinaturas</li>
                        <li>Encontre "FinZiin"</li>
                        <li>Toque em "Cancelar assinatura"</li>
                        <li>Siga as instruções na tela</li>
                      </ol>
                    </div>
                  </div>

                  <div className='grid md:grid-cols-2 gap-6'>
                    <div className='bg-green-50 p-4 rounded-lg'>
                      <h4 className='font-semibold text-green-800 mb-2'>
                        ✅ Direito a Reembolso:
                      </h4>
                      <ul className='text-sm text-green-700 space-y-1'>
                        <li>Primeiras 48 horas: reembolso integral</li>
                        <li>Problemas técnicos comprovados</li>
                        <li>Cobrança indevida ou erro da Play Store</li>
                      </ul>
                    </div>

                    <div className='bg-red-50 p-4 rounded-lg'>
                      <h4 className='font-semibold text-red-800 mb-2'>
                        ❌ Sem Reembolso:
                      </h4>
                      <ul className='text-sm text-red-700 space-y-1'>
                        <li>
                          Após 48h, mudanças de opinião não geram reembolso
                        </li>
                        <li>Período já utilizado da assinatura</li>
                        <li>Cancelamento após renovação concluída</li>
                      </ul>
                    </div>
                  </div>

                  <div className='bg-blue-50 border border-blue-200 p-4 rounded-lg'>
                    <div className='flex items-start space-x-3'>
                      <Mail className='w-5 h-5 text-blue-600 mt-0.5' />
                      <div>
                        <h4 className='font-semibold text-blue-800 mb-1'>
                          Solicitar Reembolso:
                        </h4>
                        <p className='text-sm text-blue-700'>
                          Envie email para{' '}
                          <strong>finziin.app@gmail.com</strong> com o número do
                          pedido e motivo. Responderemos em até 5 dias úteis.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5. Responsabilidades do Usuário */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Shield className='w-6 h-6 mr-3 text-orange-500' />
                  5. Responsabilidades do Usuário
                </h3>

                <div className='grid md:grid-cols-2 gap-6'>
                  <div>
                    <h4 className='font-semibold text-gray-800 mb-3'>
                      ✅ Você deve:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm text-gray-600'>
                      <li>Fornecer informações verdadeiras e completas</li>
                      <li>Manter suas credenciais e dispositivos seguros</li>
                      <li>Usar o app de forma legal e responsável</li>
                      <li>Respeitar limites de uso e regras do app</li>
                      <li>
                        Manter o app atualizado para garantir funcionamento
                        correto
                      </li>
                      <li>Reportar qualquer problema de segurança ou falha</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className='font-semibold text-gray-800 mb-3'>
                      ❌ Você não deve:
                    </h4>
                    <ul className='list-disc list-inside space-y-1 text-sm text-gray-600'>
                      <li>Compartilhar sua conta com terceiros</li>
                      <li>Tentar burlar limitações do app ou explorar bugs</li>
                      <li>
                        Realizar engenharia reversa ou modificações não
                        autorizadas
                      </li>
                      <li>Usar o app para fins ilegais</li>
                      <li>
                        Sobrecarregar ou comprometer servidores e serviços
                      </li>
                      <li>
                        Violar direitos de terceiros, incluindo propriedade
                        intelectual
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* 6. Limitações de Responsabilidade */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <AlertCircle className='w-6 h-6 mr-3 text-yellow-500' />
                  6. Limitações de Responsabilidade
                </h3>

                <div className='bg-yellow-50 border border-yellow-200 p-6 rounded-lg'>
                  <div className='space-y-3 text-sm text-gray-700'>
                    <p>
                      <strong>
                        O FinZiin é fornecido "como está" e "conforme
                        disponível".{` `}
                      </strong>
                      Fazemos nosso melhor para garantir a disponibilidade e
                      precisão, mas não garantimos:
                    </p>
                    <ul className='list-disc list-inside space-y-1 ml-4'>
                      <li>
                        Serviço sempre disponível ou livre de interrupções
                      </li>
                      <li>
                        Funcionalidades perfeitas em todos os dispositivos
                      </li>
                      <li>Dados 100% precisos em todos os momentos</li>
                      <li>
                        Atendimento completo de todas as necessidades
                        individuais
                      </li>
                    </ul>
                    <p className='font-medium text-yellow-800 mt-4'>
                      Nossa responsabilidade máxima está limitada ao valor pago
                      pela assinatura nos últimos 12 meses.
                    </p>
                  </div>
                </div>
              </div>

              {/* 7. Alterações nos Termos */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <RotateCcw className='w-6 h-6 mr-3 text-purple-500' />
                  7. Alterações nos Termos
                </h3>

                <div className='space-y-4'>
                  <p className='text-gray-600'>
                    Podemos atualizar estes Termos de Uso periodicamente para
                    refletir mudanças nos serviços, legislação ou práticas
                    comerciais.
                  </p>
                  <div className='bg-purple-50 p-4 rounded-lg'>
                    <h4 className='font-semibold text-purple-800 mb-2'>
                      📢 Como você será notificado:
                    </h4>
                    <ul className='text-sm text-purple-700 space-y-1'>
                      <li>Notificação por email (se fornecido)</li>
                      <li>Notificação dentro do aplicativo</li>
                      <li>Aviso na Google Play Store, se aplicável</li>
                    </ul>
                  </div>
                  <div className='bg-blue-50 p-4 rounded-lg'>
                    <p className='text-sm text-blue-700'>
                      Alterações significativas serão comunicadas com 30 dias de
                      antecedência. O uso contínuo do aplicativo após as
                      alterações constitui aceitação dos novos termos.
                    </p>
                  </div>
                </div>
              </div>

              {/* 8. Lei Aplicável e Jurisdição */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Gavel className='w-6 h-6 mr-3 text-red-500' />
                  8. Lei Aplicável e Jurisdição
                </h3>

                <div className='bg-gray-50 p-6 rounded-lg'>
                  <div className='space-y-3 text-gray-600'>
                    <p>
                      <strong>Lei Aplicável:</strong> Estes termos são regidos
                      pelas leis da República Federativa do Brasil, incluindo
                      LGPD, Marco Civil da Internet e Código de Defesa do
                      Consumidor.
                    </p>
                    <p>
                      <strong>Jurisdição:</strong> Qualquer disputa será
                      resolvida nos tribunais brasileiros, preferencialmente na
                      comarca onde você reside.
                    </p>
                    <p>
                      <strong>Resolução de Conflitos:</strong> Recomendamos
                      tentativa de solução amigável antes de ações legais. Entre
                      em contato conosco primeiro.
                    </p>
                  </div>
                </div>
              </div>

              {/* 9. Contato */}
              <div>
                <h3 className='text-2xl font-bold mb-4 text-gray-800 flex items-center'>
                  <Mail className='w-6 h-6 mr-3 text-blue-500' />
                  9. Contato e Suporte
                </h3>

                <div className='bg-blue-50 p-6 rounded-lg'>
                  <p className='text-blue-800 mb-4'>
                    Para dúvidas sobre estes termos, assinaturas ou qualquer
                    questão relacionada ao FinZiin:
                  </p>

                  <div className='space-y-3'>
                    <div className='flex items-center space-x-3'>
                      <Mail className='w-5 h-5 text-blue-600' />
                      <div>
                        <div className='font-semibold text-blue-800'>
                          Email Principal:
                        </div>
                        <div className='text-blue-600'>
                          finziin.app@gmail.com
                        </div>
                      </div>
                    </div>

                    <div className='flex items-center space-x-3'>
                      <Clock className='w-5 h-5 text-blue-600' />
                      <div>
                        <div className='font-semibold text-blue-800'>
                          Tempo de Resposta:
                        </div>
                        <div className='text-blue-600'>
                          Até 48 horas em dias úteis
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className='mt-4 pt-4 border-t border-blue-200'>
                    <p className='text-blue-700 text-sm'>
                      Nossa equipe de suporte responde em até 48 horas úteis
                      para todas as dúvidas sobre o aplicativo ou assinaturas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className='text-center mt-16'>
          <div className='bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-2xl p-8 max-w-2xl mx-auto'>
            <h3 className='text-2xl font-bold mb-4'>
              Dúvidas sobre os termos ou assinaturas?
            </h3>
            <p className='text-purple-100 mb-6'>
              Nossa equipe está pronta para esclarecer qualquer questão sobre
              nossos termos de uso e políticas de assinatura.
            </p>
            <a
              href='mailto:finziin.app@gmail.com'
              className='inline-flex items-center bg-white text-purple-600 font-bold py-3 px-6 rounded-xl hover:bg-gray-100 transition-colors duration-300'
            >
              <Mail className='w-5 h-5 mr-2' />
              Entrar em Contato
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TermsOfService;
